import { FC } from 'react';
import { Z } from '@components/v4';
import { PaulinaChatHeader } from './PaulinaChatHeader';
import { PaulinaConversation } from './PaulinaConversation';
import { PaulinaContextValue } from './PaulinaProvider';
import { usePaulinaDialogA11y } from './usePaulinaDialogA11y';

/**
 * PaulinaPanel — el chat ABIERTO en DESKTOP (Opción A). Anclado abajo-derecha,
 * z-index `Z.paulinaSheet` (95, DEBAJO del toast de pedido `Z.pedido`=99 de
 * NotificationCenterV2). Cuando hay pedido entrante el panel CEDE: se desplaza hacia
 * arriba (`bottom`) para no quedar tapado por el toast (que en desktop ocupa
 * `bottom-0 right-0`, ~400px de ancho).
 *
 * A11y: diálogo modal (`role=dialog` + `aria-modal` + focus-trap + retorno de foco al
 * activador + Escape) vía `usePaulinaDialogA11y`, modelo QuickPresentationsSheet. La
 * transición de `bottom` (animación NUEVA, la cesión ante pedidos) se desactiva con
 * `motion-reduce:transition-none` para respetar `prefers-reduced-motion`.
 *
 * Reusa el header (PaulinaChatHeader) y el render de mensajes/input
 * (PaulinaConversation) compartidos con el bottom-sheet móvil.
 */

type PaulinaPanelProps = Pick<
  PaulinaContextValue,
  'turns' | 'input' | 'setInput' | 'busy' | 'error' | 'bottomRef' | 'goTo' | 'send' | 'setOpen' | 'clearConversation'
> & { hayPedido: boolean; onDeactivate?: () => void };

export const PaulinaPanel: FC<PaulinaPanelProps> = ({
  turns,
  input,
  setInput,
  busy,
  error,
  bottomRef,
  goTo,
  send,
  setOpen,
  clearConversation,
  hayPedido,
  onDeactivate,
}) => {
  const close = () => setOpen(false);
  const dialogRef = usePaulinaDialogA11y(close);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Chat con Paulina"
      style={{ zIndex: Z.paulinaSheet }}
      className={`fixed right-6 flex h-[65vh] max-h-[540px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-xl border border-[#e2e8f0] bg-[#ffffff] text-[#253746] shadow-2xl transition-[bottom] duration-300 motion-reduce:transition-none ${
        // Cede el paso al toast de pedido (z-99): se eleva sobre el área que ocupa.
        hayPedido ? 'bottom-[calc(20px+360px)]' : 'bottom-6'
      }`}
    >
      <PaulinaChatHeader
        busy={busy}
        emptyConversation={turns.length === 0}
        onClear={clearConversation}
        onClose={close}
        onDeactivate={onDeactivate}
      />
      <PaulinaConversation
        turns={turns}
        input={input}
        setInput={setInput}
        busy={busy}
        error={error}
        bottomRef={bottomRef}
        goTo={goTo}
        send={send}
      />
    </div>
  );
};
